import { useEffect, useState } from 'react';
import { fetchUsers, setUserRole } from '../api/users';
import { useAuth } from '../context/AuthContext';
import type { UserRow, Role } from '../types';

const ROLES: Role[] = ['viewer', 'operator', 'admin'];
const ROLE_LABEL: Record<Role, string> = {
  viewer: 'viewer (consultant)',
  operator: 'operator (editeur)',
  admin: 'admin',
};

export function UsersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [enabled, setEnabled] = useState(true);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // add-user form
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<Role>('viewer');

  function load() {
    setLoading(true);
    setError(null);
    fetchUsers()
      .then((r) => {
        setEnabled(r.data.enabled);
        setUsers(r.data.users);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin]);

  async function changeRole(email: string, role: Role) {
    setBusy(email);
    try {
      await setUserRole(email, role);
      load();
    } catch (e) {
      alert((e as Error).message);
      setBusy(null);
    }
  }

  async function addUser(e: React.FormEvent) {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setBusy(email);
    try {
      await setUserRole(email, newRole);
      setNewEmail('');
      setNewRole('viewer');
      load();
    } catch (err) {
      alert((err as Error).message);
      setBusy(null);
    }
  }

  if (!isAdmin) {
    return <div className="text-[13px] t-muted">Reserve aux administrateurs.</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="page-title">Utilisateurs</h1>

      {loading && <div className="text-[13px] t-muted">Chargement...</div>}
      {error && !loading && <div className="text-red-400">{error}</div>}

      {!loading && !error && !enabled && (
        <div className="text-[13px] rounded-md border note-amber px-3 py-2">
          L'annuaire des utilisateurs n'est pas encore active (couche d'ecriture non provisionnee). Les roles sont
          geres via le secret BACKOFFICE_ALLOWLIST en attendant. Voir db/phase1/020_backoffice_writes.md.
        </div>
      )}

      {!loading && !error && enabled && (
        <>
          <p className="text-[13px] t-muted">
            L'annuaire surcharge l'allowlist (qui reste le bootstrap des premiers admins). Les utilisateurs
            apparaissent apres leur premiere connexion ; un changement de role prend effet a leur prochaine connexion.
          </p>

          {/* Add / pre-provision a user */}
          <form onSubmit={addUser} className="panel p-5 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <label htmlFor="new-email" className="label">
                Ajouter / pre-provisionner un email
              </label>
              <input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="prenom.nom@zce-group.com"
                className="input"
              />
            </div>
            <div>
              <label htmlFor="new-role" className="label">
                Role
              </label>
              <select
                id="new-role"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as Role)}
                className="input w-auto"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn" disabled={!newEmail.trim()}>
              Enregistrer
            </button>
          </form>

          <div className="panel p-0 overflow-hidden">
            <div className="table-wrap">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="th">Email</th>
                    <th className="th">Nom</th>
                    <th className="th">Role</th>
                    <th className="th">Derniere connexion</th>
                    <th className="th">Modifie par</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={5}>
                        <div className="empty-state text-[13px] t-muted">
                          Aucun utilisateur enregistre pour l'instant.
                        </div>
                      </td>
                    </tr>
                  )}
                  {users.map((u) => {
                    const self = u.email.toLowerCase() === user?.email.toLowerCase();
                    return (
                      <tr key={u.email} className="tr-hover">
                        <td className="td font-medium t-strong">
                          {u.email}
                          {self && <span className="ml-2 text-xs t-muted">(vous)</span>}
                        </td>
                        <td className="td">{u.name || '-'}</td>
                        <td className="td">
                          <select
                            value={u.role}
                            disabled={self || busy === u.email}
                            onChange={(e) => changeRole(u.email, e.target.value as Role)}
                            className="input h-8 w-auto"
                          >
                            {ROLES.map((r) => (
                              <option key={r} value={r}>
                                {ROLE_LABEL[r]}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="td num t-muted">{u.lastLogin ? u.lastLogin.slice(0, 19) : '-'}</td>
                        <td className="td t-muted">{u.updatedBy || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
